import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import times from 'lodash/times';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import SkipModal from './components/SkipModal';
import { Icon, Box } from 'core/ui/atoms';

const CalendarPicker = ({ daysPerWeek, deliverySkips, refetchSkips, createDeliverySkips, destroyDeliverySkips }) => {
  const [value, setValue] = useState(new Date());
  const [selectedSkip, setSelectedSkip] = useState(null);
  const [confirmedSkip, setConfirmedSkip] = useState(false);

  function getDatesInRange(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dayCount = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

    return times(dayCount, (i) => {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        const year = date.getFullYear();
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${day}-${month}`;
    });
  }

  const skippedWeekDates = deliverySkips
    .filter((item) => item.skipped === true)
    .map((item) => {
        const startDate = item.deliveryPeriod.startDate;
        const endDate = item.deliveryPeriod.endDate;

        return getDatesInRange(startDate, endDate);
    })
    .flat();

  const onCalendarChange = async (date) => {
    const startOfWeek = moment(date).startOf('week').add(1, 'day').toLocaleString();
    const formattedDate = moment(startOfWeek).format('YYYY-DD-MM');
    const searchedSkip = deliverySkips.find((skip) => moment(skip.deliveryPeriod.startDate).format('YYYY-DD-MM') == formattedDate);

    setSelectedSkip(searchedSkip);
  };

  const handleSkipConfirmation = async () => {
    if (selectedSkip?.deliveryPeriod?.id) {
      try {
        await createDeliverySkips({
          variables: {
            input: {
              deliveryPeriodIds: [selectedSkip.deliveryPeriod.id],
            },
          },
        });
        refetchSkips();
      } catch (error) {
        console.error('Failed to create delivery skips:', error);
      }
    }
    setConfirmedSkip(false);
    setSelectedSkip(null);
    setValue(new Date());
  };

  const handleUnSkipConfirmation = async () => {
    if (selectedSkip?.deliveryPeriod?.id) {
      try {
        await destroyDeliverySkips({
          variables: {
            input: {
              deliveryPeriodIds: [selectedSkip.deliveryPeriod.id],
            },
          },
        });
        refetchSkips();
      } catch (error) {
        console.error('Failed to create delivery skips:', error);
      }
    }
    setConfirmedSkip(false);
    setSelectedSkip(null);
    setValue(new Date());
  };

  const disabledDates = skippedWeekDates;

  const isWeekday = (date) => {
    const dayOfWeek = moment(date, 'YYYY-DD-MM').format('ddd');
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu'];
    return weekdays.slice(0, daysPerWeek).includes(dayOfWeek);
  };

  const isDisabledDate = (date) => disabledDates.includes(date.toString());

  const tileClassName = ({ date }) => {
    const dateCheck = moment(date).format('YYYY-DD-MM');
    if (isDisabledDate(dateCheck)) return 'disabled-date-tile';
    return isWeekday(dateCheck) ? 'weekday-tile' : 'weekend-tile';
  };

  const closeModal = () => setSelectedSkip(null);

  return (
    <Box>
      <StyledCalendar
        prev2Label={null}
        next2Label={null}
        defaultActiveStartDate={null}
        prevLabel={<Caret icon='arrow_l' />}
        nextLabel={<Caret icon='arrow_r' />}
        onChange={(value) => onCalendarChange(value)}
        value={value}
        tileClassName={tileClassName}
      />
      {!isEmpty(selectedSkip) && (
        <SkipModal
          selectedSkip={selectedSkip}
          confirmedSkip={confirmedSkip}
          setConfirmedSkip={setConfirmedSkip}
          closeModal={closeModal}
          handleUnSkipConfirmation={handleUnSkipConfirmation}
          handleSkipConfirmation={handleSkipConfirmation}
        />
      )}
    </Box>
  );
};

CalendarPicker.propTypes = {
  deliverySkips: PropTypes.array.isRequired,
  refetchSkips: PropTypes.func.isRequired,
  daysPerWeek: PropTypes.number,
  createDeliverySkips: PropTypes.func.isRequired,
  destroyDeliverySkips: PropTypes.func.isRequired,
  setErrorMsg: PropTypes.func,
  setInfoMessage: PropTypes.func,
};

const StyledCalendar = styled(Calendar)`
  /* Base calendar layout - essential for proper rendering */
  width: 100%;
  max-width: 100%;
  background: white;
  border: 1px solid #a0a096;
  font-family: Arial, Helvetica, sans-serif;
  line-height: 1.125em;

  /* Ensure proper box-sizing for all elements */
  *, *:before, *:after {
    box-sizing: border-box;
  }

  /* Navigation styles */
  .react-calendar__navigation {
    display: flex;
    height: 44px;
    margin-bottom: 1em;
    background: #36443A;
    margin-bottom: 0px;
  }

  .react-calendar__navigation button {
    min-width: 44px;
    background: none;
    margin: 0;
    border: 0;
    outline: none;
    color: #FFFFFF;
  }

  .react-calendar__navigation button:enabled:hover {
    cursor: pointer;
    background-color: unset;
  }

  /* Weekdays header */
  .react-calendar__month-view__weekdays {
    background: ${({ theme }) => theme.colors.creamWhite};
    padding-top: 10px;
    padding-bottom: 10px;
    display: flex;
    flex-wrap: nowrap;
    text-align: center;
  }

  /* Tile base styles */
  .react-calendar__tile {
    max-width: 100%;
    padding: 10px 6.6667px;
    background: none;
    text-align: center;
    line-height: 16px;
    font: inherit;
    font-size: 0.833em;
    margin: 0;
    border: 0;
    outline: none;
  }

  .react-calendar__tile:enabled:hover {
    cursor: pointer;
  }

  .react-calendar__tile--active:enabled:hover {
    background: #C5D1C0;
  }

  .react-calendar button {
    ${(props) => props.isWeekday && `
      background: #C5D1C0;
      color: white;
    `}
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: #36443A;
    abbr {
      opacity: 0.6;
    }
  }

  /* Custom tile classes */
  .weekday-tile {
    background-color: #C5D1C0;
    color: #333;
  }

  .weekend-tile {
    background-color: #f9f9f9;
    color: #333;
  }

  .disabled-date-tile {
    color: #36443A;
    opacity: 0.4;
    background: #C5D1C0;
  }
`;

const Caret = styled(Icon)`
  display: inline-block;
  transition: transform 0.25s ease-in-out;
  transform-style: preserve-3d;
  transform: rotateX(${(p) => p.$open ? '180deg' : '0deg' });
`;

export default CalendarPicker;
